import React, { useState } from "react";
import { Form, Input, Row, Col, Card, Typography, Button, Select, Space } from "antd";
import { PlusOutlined, MinusCircleOutlined, EnvironmentOutlined, FileImageOutlined } from "@ant-design/icons";
import { FileBrowserWizard } from "../../../components/FileBrowserWizard";

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

interface AttractionsTabProps {
  form: any;
}

export const AttractionsTab: React.FC<AttractionsTabProps> = ({ form }) => {
  const [fileBrowserVisible, setFileBrowserVisible] = useState(false);
  const [currentFieldPath, setCurrentFieldPath] = useState<(string | number)[]>([]);

  // Interface für MediaFile
  interface MediaFile {
    uid: string;
    name: string;
    size: number;
    uploadDate: string;
    url: string;
    type: string;
  }

  const handleSelectImage = (fieldPath: (string | number)[]) => {
    setCurrentFieldPath(fieldPath);
    setFileBrowserVisible(true);
  };

  const handleImageSelected = (selectedFiles: MediaFile[]) => {
    if (selectedFiles.length > 0) {
      // Setze die URL des ersten ausgewählten Bildes in das entsprechende Formularfeld
      form.setFieldValue(currentFieldPath, selectedFiles[0].url);
    }
  };

  return (
    <div>
      <Title level={4}>
        <EnvironmentOutlined style={{ marginRight: '8px' }} />
        Sehenswürdigkeiten & Ausflüge
      </Title>
      
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Sehenswürdigkeiten" size="small">
            <Form.List name={['content', 'attractions']}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card 
                      key={key} 
                      size="small" 
                      style={{ marginBottom: '16px' }}
                      extra={
                        <Button 
                          type="text" 
                          danger 
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        />
                      }
                    >
                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'name']}
                            label="Name"
                            rules={[{ required: true, message: 'Bitte geben Sie einen Namen ein' }]}
                          >
                            <Input placeholder="z.B. Historisches Schloss" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'category']}
                            label="Kategorie"
                            rules={[{ required: true, message: 'Bitte wählen Sie eine Kategorie' }]}
                          >
                            <Select placeholder="Kategorie auswählen">
                              <Option value="Kultur">Kultur</Option>
                              <Option value="Natur">Natur</Option>
                              <Option value="Erholung">Erholung</Option>
                              <Option value="Sport">Sport</Option>
                              <Option value="Geschichte">Geschichte</Option>
                              <Option value="Kunst">Kunst</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'distance']}
                            label="Entfernung"
                          >
                            <Input placeholder="z.B. 15 min zu Fuß" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'image']}
                            label="Bild-URL"
                          >
                            <Space.Compact style={{ width: '100%' }}>
                            <Input placeholder="https://example.com/image.jpg" />
                              <Button 
                                icon={<FileImageOutlined />}
                                onClick={() => handleSelectImage(['attractions', name, 'image'])}
                                title="Bild aus Medienablage wählen"
                              >
                                Wählen
                              </Button>
                            </Space.Compact>
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            {...restField}
                            name={[name, 'description']}
                            label="Beschreibung"
                          >
                            <TextArea 
                              rows={3} 
                              placeholder="Detaillierte Beschreibung der Sehenswürdigkeit..."
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'openingHours']}
                            label="Öffnungszeiten"
                          >
                            <Input placeholder="z.B. 09:00 - 18:00 Uhr" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'entranceFee']}
                            label="Eintrittspreis"
                          >
                            <Input placeholder="z.B. 8€ Erwachsene" />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Form.Item>
                    <Button 
                      type="dashed" 
                      onClick={() => add()} 
                      block 
                      icon={<PlusOutlined />}
                    >
                      Sehenswürdigkeit hinzufügen
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
        <Col span={24}>
          <Card title="Empfohlene Routen" size="small">
            <Form.List name={['content', 'routes']}>
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card 
                      key={key} 
                      size="small" 
                      style={{ marginBottom: '16px' }}
                      extra={
                        <Button 
                          type="text" 
                          danger 
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(name)}
                        />
                      }
                    >
                      <Row gutter={[8, 8]}>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'name']}
                            label="Routenname"
                            rules={[{ required: true, message: 'Bitte geben Sie einen Namen ein' }]}
                          >
                            <Input placeholder="z.B. Stadtspaziergang" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'duration']}
                            label="Dauer"
                          >
                            <Input placeholder="z.B. 2 Stunden" />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'type']}
                            label="Typ"
                          >
                            <Select placeholder="Typ auswählen">
                              <Option value="walking">Zu Fuß</Option>
                              <Option value="cycling">Mit dem Fahrrad</Option>
                              <Option value="car">Mit dem Auto</Option>
                              <Option value="public">Öffentliche Verkehrsmittel</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'difficulty']}
                            label="Schwierigkeit"
                          >
                            <Select placeholder="Schwierigkeit auswählen">
                              <Option value="easy">Einfach</Option>
                              <Option value="medium">Mittel</Option>
                              <Option value="hard">Schwer</Option>
                            </Select>
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            {...restField}
                            name={[name, 'description']}
                            label="Routenbeschreibung"
                          >
                            <TextArea 
                              rows={3} 
                              placeholder="Detaillierte Beschreibung der Route..."
                            />
                          </Form.Item>
                        </Col>
                        <Col span={24}>
                          <Form.Item
                            {...restField}
                            name={[name, 'waypoints']}
                            label="Wegpunkte"
                          >
                            <TextArea 
                              rows={2} 
                              placeholder="Startpunkt → Wegpunkt 1 → Wegpunkt 2 → Endpunkt"
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </Card>
                  ))}
                  <Form.Item>
                    <Button 
                      type="dashed" 
                      onClick={() => add()} 
                      block 
                      icon={<PlusOutlined />}
                    >
                      Route hinzufügen
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Card>
        </Col>
      </Row>

      {/* FileBrowser Wizard */}
      <FileBrowserWizard
        visible={fileBrowserVisible}
        onClose={() => setFileBrowserVisible(false)}
        onSelect={handleImageSelected}
        title="Bild für Sehenswürdigkeit auswählen"
        multiple={false}
        maxSelection={1}
      />
    </div>
  );
}; 